import { c as _c } from "react/compiler-runtime";
import { IcoTrash16 } from '@onefootprint/icons';
import type { ApiNodeData, GraphOperationRemoveNode } from '@onefootprint/request-types/dashboard';
import { Dialog, LoadingSpinner } from '@onefootprint/ui';
import { cx } from 'class-variance-authority';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import useEdit from '@/playbook-builder/hooks/use-edit';
import useStore from '../../hooks/use-store';
import { useGetStaticNodeInfo } from '../base-node/hooks/use-get-static-node-info';
type RemoveButtonProps = {
  data: ApiNodeData;
  operation: GraphOperationRemoveNode;
} & Pick<React.ButtonHTMLAttributes<HTMLButtonElement>, 'className'>;
const RemoveButton = t0 => {
  const $ = _c(61);
  const {
    operation,
    data,
    className
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      keyPrefix: "removeNode"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation("playbook-builder", t1);
  const getNodeInfo = useGetStaticNodeInfo();
  const mutation = useEdit();
  const {
    setNodeHighlight
  } = useStore();
  const [showConfirmation, setShowConfirmation] = useState(false);
  let T0;
  let t10;
  let t11;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[1] !== className || $[2] !== data.kind || $[3] !== getNodeInfo || $[4] !== mutation || $[5] !== operation || $[6] !== setNodeHighlight || $[7] !== showConfirmation || $[8] !== t) {
    const nodeKind = getNodeInfo(data.kind).title.lowercase;
    const treeKind = operation.nodeIdsToDelete.length > 1 ? t(_temp) : t(_temp2);
    const label = t(_temp3, {
      nodeKind,
      treeKind
    });
    let t12;
    if ($[20] !== mutation || $[21] !== operation) {
      t12 = async () => {
        ;
        try {
          await mutation.mutateAsync(operation);
        } catch (t13) {
          const error = t13;
          console.error("Error updating playbook session:", error);
        }
      };
      $[20] = mutation;
      $[21] = operation;
      $[22] = t12;
    } else {
      t12 = $[22];
    }
    const handleRemove = t12;
    let t13;
    if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
      t13 = async () => setShowConfirmation(true);
      $[23] = t13;
    } else {
      t13 = $[23];
    }
    const handleShowConfirmation = t13;
    let t14;
    if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
      t14 = () => setShowConfirmation(false);
      $[24] = t14;
    } else {
      t14 = $[24];
    }
    const handleClose = t14;
    let t15;
    if ($[25] !== operation.nodeIdsToDelete || $[26] !== setNodeHighlight) {
      t15 = () => setNodeHighlight({
        color: "error",
        ids: operation.nodeIdsToDelete
      });
      $[25] = operation.nodeIdsToDelete;
      $[26] = setNodeHighlight;
      $[27] = t15;
    } else {
      t15 = $[27];
    }
    const handleMouseEnter = t15;
    let t16;
    if ($[28] !== setNodeHighlight) {
      t16 = () => setNodeHighlight(undefined);
      $[28] = setNodeHighlight;
      $[29] = t16;
    } else {
      t16 = $[29];
    }
    const handleMouseLeave = t16;
    let t17;
    if ($[30] !== className) {
      t17 = cx("cursor-pointer", "flex flex-shrink-0 justify-center items-center w-7 h-7 rounded-full", "bg-error-inverted hover:bg-error-inverted/80", "shadow-[0px_1px_8px_0px_rgba(0,0,0,0.08)]", className);
      $[30] = className;
      $[31] = t17;
    } else {
      t17 = $[31];
    }
    let t18;
    if ($[32] !== mutation.isPending) {
      t18 = mutation.isPending ? <LoadingSpinner size="compact" color="tertiary" /> : <IcoTrash16 color="quinary" />;
      $[32] = mutation.isPending;
      $[33] = t18;
    } else {
      t18 = $[33];
    }
    t11 = <button aria-label={label} type="button" className={t17} onClick={handleShowConfirmation} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>{t18}</button>;
    T0 = Dialog;
    t4 = showConfirmation;
    t5 = handleClose;
    t6 = true;
    t7 = "compact";
    t8 = label;
    let t19;
    if ($[34] !== t) {
      t19 = t(_temp4);
      $[34] = t;
      $[35] = t19;
    } else {
      t19 = $[35];
    }
    if ($[36] !== handleRemove || $[37] !== mutation.isPending || $[38] !== t19) {
      t9 = {
        label: t19,
        onClick: handleRemove,
        loading: mutation.isPending,
        variant: "destructive"
      };
      $[36] = handleRemove;
      $[37] = mutation.isPending;
      $[38] = t19;
      $[39] = t9;
    } else {
      t9 = $[39];
    }
    let t20;
    if ($[40] !== t) {
      t20 = t(_temp5);
      $[40] = t;
      $[41] = t20;
    } else {
      t20 = $[41];
    }
    if ($[42] !== mutation.isPending || $[43] !== t20) {
      t10 = {
        label: t20,
        onClick: handleClose,
        disabled: mutation.isPending
      };
      $[42] = mutation.isPending;
      $[43] = t20;
      $[44] = t10;
    } else {
      t10 = $[44];
    }
    t2 = "text-center text-body-2 text-secondary";
    t3 = t(_temp6, {
      nodeKind,
      subtree: operation.nodeIdsToDelete.length > 1 ? t(_temp7) : ""
    });
    $[1] = className;
    $[2] = data.kind;
    $[3] = getNodeInfo;
    $[4] = mutation;
    $[5] = operation;
    $[6] = setNodeHighlight;
    $[7] = showConfirmation;
    $[8] = t;
    $[9] = T0;
    $[10] = t10;
    $[11] = t11;
    $[12] = t2;
    $[13] = t3;
    $[14] = t4;
    $[15] = t5;
    $[16] = t6;
    $[17] = t7;
    $[18] = t8;
    $[19] = t9;
  } else {
    T0 = $[9];
    t10 = $[10];
    t11 = $[11];
    t2 = $[12];
    t3 = $[13];
    t4 = $[14];
    t5 = $[15];
    t6 = $[16];
    t7 = $[17];
    t8 = $[18];
    t9 = $[19];
  }
  let t12;
  if ($[45] !== t2 || $[46] !== t3) {
    t12 = <div className={t2}>{t3}</div>;
    $[45] = t2;
    $[46] = t3;
    $[47] = t12;
  } else {
    t12 = $[47];
  }
  let t13;
  if ($[48] !== T0 || $[49] !== t10 || $[50] !== t12 || $[51] !== t4 || $[52] !== t5 || $[53] !== t6 || $[54] !== t7 || $[55] !== t8 || $[56] !== t9) {
    t13 = <T0 open={t4} onClose={t5} isConfirmation={t6} size={t7} title={t8} primaryButton={t9} secondaryButton={t10}>{t12}</T0>;
    $[48] = T0;
    $[49] = t10;
    $[50] = t12;
    $[51] = t4;
    $[52] = t5;
    $[53] = t6;
    $[54] = t7;
    $[55] = t8;
    $[56] = t9;
    $[57] = t13;
  } else {
    t13 = $[57];
  }
  let t14;
  if ($[58] !== t11 || $[59] !== t13) {
    t14 = <>{t11}{t13}</>;
    $[58] = t11;
    $[59] = t13;
    $[60] = t14;
  } else {
    t14 = $[60];
  }
  return t14;
};
export default RemoveButton;
function _temp($) {
  return $.subtree;
}
function _temp2($_0) {
  return $_0.node;
}
function _temp3($_1) {
  return $_1.title;
}
function _temp4($_2) {
  return $_2.cta;
}
function _temp5($_3) {
  return $_3.cancel;
}
function _temp6($_4) {
  return $_4.description;
}
function _temp7($_5) {
  return $_5.andItsSubtree;
}